import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';

const JobNode = ({ data, isConnectable }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#e74c3c';
      case 'high': return '#f39c12';
      case 'medium': return '#3498db';
      case 'low': return '#95a5a6';
      default: return '#3498db';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'scheduled': return '#3498db';
      case 'running': return '#e74c3c';
      case 'completed': return '#27ae60';
      case 'failed': return '#c0392b';
      default: return '#95a5a6';
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const priorityColor = getPriorityColor(data.priority);
  const statusColor = getStatusColor(data.status);

  return (
    <div className={`job-node ${data.status} ${data.isNew ? 'new-job' : ''}`}>
      {/* Input handle for dependencies */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#555' }}
        onConnect={(params) => console.log('handle onConnect', params)}
        isConnectable={isConnectable}
      />

      {/* Node Header */}
      <div className="node-header" style={{ borderLeftColor: priorityColor }}>
        <div className="node-title">
          <span className="job-icon">⚙️</span>
          <span className="job-name">{data.label}</span>
        </div>
        <div className="node-actions">
          {data.onEdit && (
            <button
              className="node-btn edit-btn"
              onClick={() => data.onEdit(data.nodeId || 'current')}
              title="Edit Job"
            >
              ✏️
            </button>
          )}
          {data.onDelete && (
            <button
              className="node-btn delete-btn"
              onClick={() => data.onDelete(data.nodeId || 'current')}
              title="Delete Job"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {/* Node Body */}
      <div className="node-body">
        <div className="job-status">
          <span 
            className="status-indicator"
            style={{ backgroundColor: statusColor }}
          ></span>
          <span className="status-text">{data.status}</span>
        </div>

        {data.estimatedDuration && (
          <div className="job-duration">
            <span className="duration-icon">⏱️</span>
            <span>{formatDuration(data.estimatedDuration)}</span>
          </div>
        )}

        {data.priority && (
          <div className="job-priority">
            <span className="priority-icon">🚨</span>
            <span style={{ color: priorityColor, fontWeight: 'bold' }}>
              {data.priority}
            </span>
          </div>
        )}

        {data.requiredCapabilities && data.requiredCapabilities.length > 0 && (
          <div className="job-capabilities">
            <div className="capabilities-header">Required:</div>
            <div className="capabilities-list">
              {data.requiredCapabilities.slice(0, 2).map((cap, index) => (
                <span key={index} className="capability-tag">
                  {cap}
                </span>
              ))}
              {data.requiredCapabilities.length > 2 && (
                <span className="capability-tag more">
                  +{data.requiredCapabilities.length - 2}
                </span>
              )}
            </div>
          </div>
        )}

        {data.assignedMachine && (
          <div className="assigned-machine">
            <span className="machine-icon">🤖</span>
            <span>{data.assignedMachine}</span>
          </div>
        )}

        {data.isNew && (
          <div className="new-job-notice">
            <span>Click to configure</span>
          </div>
        )}
      </div>

      {/* Output handle for dependencies */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ background: '#555' }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default memo(JobNode);